import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TopNavbar, NAV_ICON_SEARCH_INDEX, NAV_ICON_FAVORITES_INDEX, NAV_ICON_SETTINGS_INDEX, NAV_ICON_LOCK_INDEX, NAV_ICON_HISTORY_INDEX } from '../../components/TopNavbar/TopNavbar';
import { ContentNav } from '../../components/ContentNav/ContentNav';
import { getRuntimeTmdbLanguage, getRuntimeTmdbRegion } from '../../api/tmdbAPI';
import { getMatchedList } from '../../api/backendAPI';
import { consumeCatalogForceRefresh } from '../../utils/catalogRefresh';
import { useAuth } from '../../contexts/AuthContext';
import { useDevice } from '../../contexts/DeviceContext';
import { useParentalControls } from '../../contexts/ParentalControlsContext';
import { useLockToggle } from '../../hooks/useLockToggle';
import { useContentNavIds } from '../../hooks/useContentNavIds';
import { SeriesDetailsEntry } from '../SeriesDetails/SeriesDetails';
import { MovieDetailsEntry } from '../MovieDetails/MovieDetails';
import PasswordModal from '../../components/PasswordModal/PasswordModal';
import HubLoadingMessage from '../../components/HubLoadingMessage/HubLoadingMessage';
import './Anime.css';
import animeSeriesImg from '../../assets/anime-series.png';
import animeMoviesImg from '../../assets/anime-movies.png';

// ─── Types ─────────────────────────────────────────────────────────────────

interface AnimeSeriesItem {
  id: string;
  title?: string;
  posterUrl?: string;
  backdropUrl?: string;
  seriesStreamId?: string;
}

interface AnimeMovieItem {
  id: string;
  title?: string;
  posterUrl?: string;
  backdropUrl?: string;
  vodStreamId?: string;
}

// ─── Cache ──────────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function cacheKey(username: string, language: string) {
  return `ott_anime_page_v1_${language}_${username}`;
}

interface AnimeCache {
  series: AnimeSeriesItem[];
  movies: AnimeMovieItem[];
}

function loadCache(username: string, language: string): AnimeCache | null {
  try {
    const raw = localStorage.getItem(cacheKey(username, language));
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw) as { data: AnimeCache; ts: number };
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function saveCache(username: string, language: string, data: AnimeCache) {
  try {
    localStorage.setItem(cacheKey(username, language), JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface AnimeProps {
  isActive?: boolean;
  onBack?: () => void;
  onNavigate?: (sectionId: string) => void;
  onOpenSeriesDetails?: (entry: SeriesDetailsEntry) => void;
  onOpenMovieDetails?: (entry: MovieDetailsEntry) => void;
  onOpenSettings?: () => void;
  onOpenSearch?: () => void;
  onOpenFavorites?: () => void;
  onOpenHistory?: () => void;
}

/**
 * Layout (3 columns, L→R):
 *   [ slider + 2 movie cards ] [ portrait poster ] [ series/movies split ]
 * grid-template-columns: 2fr 1fr 1fr
 */
type FocusSection =
  | 'topnav'
  | 'contentnav'
  | 'slider'
  | 'movie-left'
  | 'movie-right'
  | 'poster'
  | 'split-series'
  | 'split-movies';
const NAV_ICON_COUNT_A = 5;

// ─── Component ───────────────────────────────────────────────────────────────

export const Anime: React.FC<AnimeProps> = ({
  isActive = true,
  onBack,
  onNavigate,
  onOpenSeriesDetails,
  onOpenMovieDetails,
  onOpenSettings,
  onOpenSearch,
  onOpenFavorites,
  onOpenHistory,
}) => {
  const { t, i18n } = useTranslation();
  const { isBlocked } = useParentalControls();
  const {
    handleLockIconClick,
    handleLockPasswordConfirm,
    handleLockPasswordCancel,
    lockModalOpen,
    lockModalMode,
    lockModalError,
  } = useLockToggle();
  const { authData } = useAuth();
  const { deviceId, activationPassword } = useDevice();

  const [series, setSeries] = useState<AnimeSeriesItem[]>([]);
  const [movies, setMovies] = useState<AnimeMovieItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [sliderIdx, setSliderIdx] = useState(0);
  const [focusSection, setFocusSection] = useState<FocusSection>('slider');
  const [topnavFocusedIdx, setTopnavFocusedIdx] = useState(0);
  const [contentnavFocusedId, setContentnavFocusedId] = useState('anime');
  const contentNavIds = useContentNavIds();

  const autoAdvRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Data fetching ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const username = authData?.user?.username ?? 'anon';
    const tmdbLanguage = getRuntimeTmdbLanguage(i18n.language);
    const tmdbRegion = getRuntimeTmdbRegion(i18n.language);

    const cached = loadCache(username, tmdbLanguage);
    if (cached) {
      setSeries(cached.series);
      setMovies(cached.movies);
      setLoading(false);
      return;
    }

    setLoading(true);

    (async () => {
      try {
        let seriesItems: AnimeSeriesItem[] = [];
        let movieItems: AnimeMovieItem[] = [];

        if (authData) {
          const [serResults, movResults] = await Promise.all([
            getMatchedList(deviceId, activationPassword, 'anime-series', tmdbLanguage, tmdbRegion, 7, 'preview', consumeCatalogForceRefresh('anime-series')),
            getMatchedList(deviceId, activationPassword, 'anime-movies', tmdbLanguage, tmdbRegion, 7, 'preview', consumeCatalogForceRefresh('anime-movies')),
          ]);
          if (cancelled) return;
          seriesItems = serResults.map(m => ({
            id: String(m.tmdbId),
            title: m.title,
            posterUrl: m.posterUrl ?? undefined,
            backdropUrl: m.backdropUrl ?? undefined,
            seriesStreamId: m.streamId,
          }));
          movieItems = movResults.map(m => ({
            id: String(m.tmdbId),
            title: m.title,
            posterUrl: m.posterUrl ?? undefined,
            backdropUrl: m.backdropUrl ?? undefined,
            vodStreamId: m.streamId,
          }));
        }

        if (!cancelled) {
          setSeries(seriesItems);
          setMovies(movieItems);
          setLoading(false);
          if (seriesItems.length || movieItems.length) saveCache(username, tmdbLanguage, { series: seriesItems, movies: movieItems });
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [authData, deviceId, activationPassword, i18n.language]);

  // ─── Slider auto-advance ────────────────────────────────────────────────
  const sliderItems = series.slice(0, 5);

  useEffect(() => {
    if (loading || !sliderItems.length) return;
    autoAdvRef.current = setInterval(
      () => setSliderIdx((prev) => (prev + 1) % sliderItems.length),
      5000,
    );
    return () => { if (autoAdvRef.current) clearInterval(autoAdvRef.current); };
  }, [loading, sliderItems.length]);

  const goToSlide = useCallback(
    (idx: number) => {
      if (autoAdvRef.current) { clearInterval(autoAdvRef.current); autoAdvRef.current = null; }
      setSliderIdx(idx);
      if (sliderItems.length) {
        autoAdvRef.current = setInterval(
          () => setSliderIdx((prev) => (prev + 1) % sliderItems.length),
          5000,
        );
      }
    },
    [sliderItems.length],
  );

  // ─── Open helpers ─────────────────────────────────────────
  const openSeries = useCallback(
    (item?: AnimeSeriesItem) => {
      if (!item) return;
      onOpenSeriesDetails?.({
        tmdbId: Number(item.id),
        title: item.title,
        posterUrl: item.posterUrl,
        backdropUrl: item.backdropUrl,
        seriesStreamId: item.seriesStreamId,
      });
    },
    [onOpenSeriesDetails],
  );

  const openMovie = useCallback(
    (item?: AnimeMovieItem) => {
      if (!item) return;
      onOpenMovieDetails?.({
        tmdbId: Number(item.id),
        title: item.title,
        posterUrl: item.posterUrl,
        backdropUrl: item.backdropUrl,
        vodStreamId: item.vodStreamId,
      });
    },
    [onOpenMovieDetails],
  );

  // ─── Keyboard navigation ────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive) return;

    const onKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
        case 'GoBack':
        case 'BrowserBack':
          e.preventDefault();
          onBack?.();
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setTopnavFocusedIdx((prev) => Math.max(0, prev - 1));
          } else if (focusSection === 'contentnav') {
            const idx = contentNavIds.indexOf(contentnavFocusedId);
            if (idx > 0) setContentnavFocusedId(contentNavIds[idx - 1]);
          } else if (focusSection === 'slider') {
            goToSlide((sliderIdx - 1 + (sliderItems.length || 1)) % (sliderItems.length || 1));
          } else if (focusSection === 'movie-right') {
            setFocusSection('movie-left');
          } else if (focusSection === 'poster') {
            setFocusSection('movie-right');
          } else if (focusSection === 'split-series' || focusSection === 'split-movies') {
            setFocusSection('poster');
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setTopnavFocusedIdx((prev) => Math.min(NAV_ICON_COUNT_A - 1, prev + 1));
          } else if (focusSection === 'contentnav') {
            const idx = contentNavIds.indexOf(contentnavFocusedId);
            if (idx < contentNavIds.length - 1) setContentnavFocusedId(contentNavIds[idx + 1]);
          } else if (focusSection === 'slider') {
            if (sliderIdx === sliderItems.length - 1) {
              setFocusSection('poster');
            } else {
              goToSlide(sliderIdx + 1);
            }
          } else if (focusSection === 'movie-left') {
            setFocusSection('movie-right');
          } else if (focusSection === 'movie-right') {
            setFocusSection('poster');
          } else if (focusSection === 'poster') {
            setFocusSection('split-series');
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (focusSection === 'contentnav') {
            setFocusSection('topnav');
          } else if (focusSection === 'slider') {
            setFocusSection('contentnav');
          } else if (focusSection === 'movie-left' || focusSection === 'movie-right') {
            setFocusSection('slider');
          } else if (focusSection === 'split-movies') {
            setFocusSection('split-series');
          } else if (focusSection === 'split-series' || focusSection === 'poster') {
            setFocusSection('contentnav');
          }
          break;

        case 'ArrowDown':
          e.preventDefault();
          if (focusSection === 'topnav') {
            setFocusSection('contentnav');
          } else if (focusSection === 'contentnav') {
            setFocusSection('slider');
          } else if (focusSection === 'slider') {
            setFocusSection('movie-left');
          } else if (focusSection === 'poster') {
            setFocusSection('split-movies');
          } else if (focusSection === 'split-series') {
            setFocusSection('split-movies');
          }
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          if (focusSection === 'topnav') {
            if (topnavFocusedIdx === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
            if (topnavFocusedIdx === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
            if (topnavFocusedIdx === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
            if (topnavFocusedIdx === NAV_ICON_LOCK_INDEX) handleLockIconClick();
            if (topnavFocusedIdx === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
          } else if (focusSection === 'contentnav') {
            if (contentnavFocusedId === 'anime') {
              setFocusSection('slider');
            } else {
              onNavigate?.(contentnavFocusedId);
            }
          } else if (focusSection === 'slider') {
            openSeries(sliderItems[sliderIdx]);
          } else if (focusSection === 'movie-left') {
            openMovie(movies[1]);
          } else if (focusSection === 'movie-right') {
            openMovie(movies[2]);
          } else if (focusSection === 'poster') {
            openMovie(movies[0]);
          } else if (focusSection === 'split-series') {
            onNavigate?.('anime-series');
          } else if (focusSection === 'split-movies') {
            onNavigate?.('anime-movies');
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isActive, focusSection, sliderIdx, sliderItems, movies,
    goToSlide, onBack, openSeries, openMovie, onNavigate,
    topnavFocusedIdx, contentnavFocusedId, contentNavIds, onOpenSearch, onOpenFavorites, onOpenSettings, onOpenHistory,
  ]);

  // ─── Derived data ────────────────────────────────────────────────────────
  const currentSlide = sliderItems[sliderIdx];

  // movies[0] → portrait poster (centre column)
  // movies[1], movies[2] → landscape cards below slider
  // movies[3] → background for movies split card
  const posterMovie   = movies[0];
  const movieCardLeft  = movies[1];
  const movieCardRight = movies[2];

  const sliderStyle: React.CSSProperties = currentSlide?.backdropUrl
    ? { backgroundImage: `url(${currentSlide.backdropUrl})` }
    : currentSlide?.posterUrl
    ? { backgroundImage: `url(${currentSlide.posterUrl})` }
    : {};

  const posterStyle: React.CSSProperties =
    !loading && posterMovie?.posterUrl
      ? { backgroundImage: `url(${posterMovie.posterUrl})` }
      : {};

  const cardLeftStyle: React.CSSProperties =
    !loading && (movieCardLeft?.backdropUrl ?? movieCardLeft?.posterUrl)
      ? { backgroundImage: `url(${movieCardLeft!.backdropUrl ?? movieCardLeft!.posterUrl})` }
      : {};

  const cardRightStyle: React.CSSProperties =
    !loading && (movieCardRight?.backdropUrl ?? movieCardRight?.posterUrl)
      ? { backgroundImage: `url(${movieCardRight!.backdropUrl ?? movieCardRight!.posterUrl})` }
      : {};

  // ─── Render ──────────────────────────────────────────────────────────────
  if (isBlocked('anime')) {
    return (
      <div className="anime anime--blocked">
        <TopNavbar
          focusedIndex={focusSection === 'topnav' ? topnavFocusedIdx : undefined}
          onIconClick={(i) => {
            if (i === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
            if (i === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
            if (i === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
            if (i === NAV_ICON_LOCK_INDEX) handleLockIconClick();
            if (i === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
          }}
        />
        <div className="anime__blocked-msg">
          <p>{t('parental.blockedMessage')}</p>
        </div>
        {lockModalOpen && (
          <PasswordModal
            mode={lockModalMode}
            error={lockModalError ? t(lockModalError) : undefined}
            onConfirm={handleLockPasswordConfirm}
            onCancel={handleLockPasswordCancel}
          />
        )}
      </div>
    );
  }
  return (
    <>
    <div className="anime">
      <TopNavbar
        focusedIndex={focusSection === 'topnav' ? topnavFocusedIdx : undefined}
        onIconClick={(i) => {
          if (i === NAV_ICON_SEARCH_INDEX) onOpenSearch?.();
          if (i === NAV_ICON_FAVORITES_INDEX) onOpenFavorites?.();
          if (i === NAV_ICON_SETTINGS_INDEX) onOpenSettings?.();
          if (i === NAV_ICON_LOCK_INDEX) handleLockIconClick();
          if (i === NAV_ICON_HISTORY_INDEX) onOpenHistory?.();
        }}
      />
      <ContentNav
        activeId="anime"
        focusedId={focusSection === 'contentnav' ? contentnavFocusedId : undefined}
        onSelect={(id) => onNavigate?.(id)}
      />

      <div className="anime__main">
        <div className="anime__layout">

          {/* ── Left column: slider + 2 movie cards ── */}
          <div className="anime__left">
            <div
              className={[
                'anime__slider',
                loading ? 'anime__slider--loading' : '',
                focusSection === 'slider' ? 'anime__slider--focused' : '',
              ].filter(Boolean).join(' ')}
              style={sliderStyle}
              onClick={() => { setFocusSection('slider'); openSeries(currentSlide); }}
            >
              {!loading && currentSlide && (
                <>
                  <div className="anime__slider-gradient" />
                  <div className="anime__slider-title">{currentSlide.title}</div>
                  <div className="anime__slider-dots">
                    {sliderItems.map((_, i) => (
                      <span
                        key={i}
                        className={`anime__slider-dot${i === sliderIdx ? ' anime__slider-dot--active' : ''}`}
                        onClick={(ev) => { ev.stopPropagation(); goToSlide(i); }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="anime__movie-cards">
              <div
                className={[
                  'anime__movie-card',
                  loading ? 'anime__movie-card--loading' : '',
                  focusSection === 'movie-left' ? 'anime__movie-card--focused' : '',
                ].filter(Boolean).join(' ')}
                style={cardLeftStyle}
                onClick={() => { setFocusSection('movie-left'); openMovie(movieCardLeft); }}
              >
                {!loading && movieCardLeft && (
                  <>
                    <div className="anime__movie-card-overlay" />
                    <span className="anime__movie-card-title">{movieCardLeft.title}</span>
                  </>
                )}
              </div>

              <div
                className={[
                  'anime__movie-card',
                  loading ? 'anime__movie-card--loading' : '',
                  focusSection === 'movie-right' ? 'anime__movie-card--focused' : '',
                ].filter(Boolean).join(' ')}
                style={cardRightStyle}
                onClick={() => { setFocusSection('movie-right'); openMovie(movieCardRight); }}
              >
                {!loading && movieCardRight && (
                  <>
                    <div className="anime__movie-card-overlay" />
                    <span className="anime__movie-card-title">{movieCardRight.title}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── Centre column: portrait poster ── */}
          <div
            className={[
              'anime__poster',
              loading ? 'anime__poster--loading' : '',
              focusSection === 'poster' ? 'anime__poster--focused' : '',
            ].filter(Boolean).join(' ')}
            style={posterStyle}
            onClick={() => { setFocusSection('poster'); openMovie(posterMovie); }}
          >
            {!loading && posterMovie && (
              <>
                <div className="anime__poster-gradient" />
                <span className="anime__poster-title">{posterMovie.title}</span>
              </>
            )}
          </div>

          {/* ── Right column: series/movies split ── */}
          <div className="anime__split">
            <div
              className={[
                'anime__split-card',
                loading ? 'anime__split-card--loading' : '',
                focusSection === 'split-series' ? 'anime__split-card--focused' : '',
              ].filter(Boolean).join(' ')}
              style={!loading ? { backgroundImage: `url(${animeSeriesImg})` } : {}}
              onClick={() => { setFocusSection('split-series'); onNavigate?.('anime-series'); }}
            >
              <div className="anime__split-card-overlay" />
              <span className="anime__split-card-title">{t('anime.series')}</span>
              <span className="anime__split-card-desc">{t('anime.seriesDesc')}</span>
            </div>

            <div
              className={[
                'anime__split-card',
                loading ? 'anime__split-card--loading' : '',
                focusSection === 'split-movies' ? 'anime__split-card--focused' : '',
              ].filter(Boolean).join(' ')}
              style={!loading ? { backgroundImage: `url(${animeMoviesImg})` } : {}}
              onClick={() => { setFocusSection('split-movies'); onNavigate?.('anime-movies'); }}
            >
              <div className="anime__split-card-overlay" />
              <span className="anime__split-card-title">{t('anime.movies')}</span>
              <span className="anime__split-card-desc">{t('anime.moviesDesc')}</span>
            </div>
          </div>

        </div>
      </div>
      <HubLoadingMessage active={loading} scope={`anime:${authData?.user?.username ?? 'anon'}`} />
    </div>
    {lockModalOpen && (
      <PasswordModal
        mode={lockModalMode}
        error={lockModalError ? t(lockModalError) : undefined}
        onConfirm={handleLockPasswordConfirm}
        onCancel={handleLockPasswordCancel}
      />
    )}
    </>
  );
};
